import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { listOptions, useConfig } from '../context/ConfigContext';

// TS port of the source (Falah) app's pages/ProcurementInvoiceForm.jsx.
// Creates a VendorInvoice (NOT a Revenue CustomerInvoice — see
// CustomerInvoiceForm.tsx for that unrelated page) via POST /vendor-invoices
// (ProcurementFulfillmentController::storeVendorInvoice()).
//
// Two corrections vs. source, verified against storeVendorInvoice()'s
// actual validation rules:
//  - `category` is a plain free-text field here (`required_without:
//    purchase_order_id|nullable|string|max:255`, no Rule::exists) — the
//    source's `expense_category` list key was dropped from the backend
//    entirely (not in ListItem::KEYS; see ListItem.php's own comment) so a
//    select bound to it would always be empty. A plain input is used
//    instead.
//  - `payment_method` list key is `payment_method` here, not the source's
//    `finance_method` — ListItem.php's comment: "'payment_method' is the
//    renamed 'finance_method' list".
interface InvoiceFormState {
  invoice_number: string;
  invoice_amount: string;
  invoice_date: string;
  due_date: string;
  vendor_name: string;
  category: string;
  payment_method: string;
  amount_paid: string;
  currency_code: string;
}

const emptyForm: InvoiceFormState = {
  invoice_number: '',
  invoice_amount: '',
  invoice_date: '',
  due_date: '',
  vendor_name: '',
  category: '',
  payment_method: '',
  amount_paid: '',
  currency_code: 'VUV',
};

const today = new Date().toISOString().slice(0, 10);

interface OrderOption {
  id: number;
  po_number: string;
  vendor_name: string;
}

interface GoodsReceiptOption {
  id: number;
  received_at: string;
  invoice_number: string | null;
}

type FormErrors = Record<string, string[]>;

export function ProcurementInvoiceForm() {
  const { lists } = useConfig();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderOption[] | null>(null);
  const [purchaseOrderId, setPurchaseOrderId] = useState('');
  const [goodsReceipts, setGoodsReceipts] = useState<GoodsReceiptOption[] | null>(null);
  const [goodsReceiptId, setGoodsReceiptId] = useState('');
  const [form, setForm] = useState<InvoiceFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [currencies, setCurrencies] = useState<string[]>([]);

  useEffect(() => {
    api.get<{ data: OrderOption[] }>('/purchase-orders', { params: { per_page: 100 } }).then((res) => setOrders(res.data.data));
    api
      .get<{ currency_code: string }[]>('/exchange-rates')
      .then((res) => setCurrencies(res.data.map((r) => r.currency_code)));
  }, []);

  useEffect(() => {
    setGoodsReceiptId('');
    if (!purchaseOrderId) {
      setGoodsReceipts(null);
      return;
    }
    api
      .get<{ data: GoodsReceiptOption[] }>('/goods-receipts', { params: { purchase_order_id: purchaseOrderId, per_page: 100 } })
      .then((res) => setGoodsReceipts(res.data.data));
  }, [purchaseOrderId]);

  const set =
    (key: keyof InvoiceFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const setInvoiceAmount = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm({ ...form, invoice_amount: value, amount_paid: value });
  };

  const selectGoodsReceipt = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setGoodsReceiptId(value);
    const receipt = goodsReceipts?.find((gr) => String(gr.id) === value);
    if (receipt) {
      setForm({
        ...form,
        invoice_number: receipt.invoice_number || form.invoice_number,
        invoice_date: receipt.received_at || form.invoice_date,
      });
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload: Record<string, unknown> = {
      ...form,
      purchase_order_id: purchaseOrderId || undefined,
      vendor_name: purchaseOrderId ? undefined : form.vendor_name,
      category: purchaseOrderId ? undefined : form.category,
      payment_method: form.payment_method || undefined,
      amount_paid: form.amount_paid || undefined,
    };
    try {
      await api.post('/vendor-invoices', payload);
      navigate('/procurement/payments');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Add payment</h1>
      </header>
      <p className="muted">
        Record a vendor invoice to start the payment approval process — an everyday expense with no formal purchase
        order can be recorded here too.
      </p>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}
          <label className="span-2">
            Purchase Order (optional)
            <select value={purchaseOrderId} onChange={(e) => setPurchaseOrderId(e.target.value)}>
              <option value="">{orders === null ? 'Loading…' : 'No purchase order — everyday expense'}</option>
              {orders?.map((po) => (
                <option key={po.id} value={po.id}>
                  {po.po_number} — {po.vendor_name}
                </option>
              ))}
            </select>
          </label>
          {purchaseOrderId && (
            <label className="span-2">
              Receiving (optional)
              <select value={goodsReceiptId} onChange={selectGoodsReceipt}>
                <option value="">
                  {goodsReceipts === null ? 'Loading…' : 'Select a goods receipt to auto-fill invoice details…'}
                </option>
                {goodsReceipts?.map((gr) => (
                  <option key={gr.id} value={gr.id}>
                    Received {gr.received_at}
                    {gr.invoice_number && ` — ${gr.invoice_number}`}
                  </option>
                ))}
              </select>
              {goodsReceipts?.length === 0 && (
                <small className="muted">No goods receipts recorded yet for this purchase order.</small>
              )}
            </label>
          )}
          {!purchaseOrderId && (
            <>
              <label>
                Vendor / payee *
                <input value={form.vendor_name} onChange={set('vendor_name')} required />
                {errors.vendor_name && <small className="error">{errors.vendor_name[0]}</small>}
              </label>
              <label>
                Category *
                <input value={form.category} onChange={set('category')} required />
                {errors.category && <small className="error">{errors.category[0]}</small>}
              </label>
            </>
          )}
          <label>
            Invoice number{purchaseOrderId ? ' *' : ''}
            <input value={form.invoice_number} onChange={set('invoice_number')} required={!!purchaseOrderId} />
            {errors.invoice_number && <small className="error">{errors.invoice_number[0]}</small>}
          </label>
          <label>
            Invoice amount *
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={form.invoice_amount}
              onChange={setInvoiceAmount}
              required
            />
            {errors.invoice_amount && <small className="error">{errors.invoice_amount[0]}</small>}
          </label>
          <label>
            Currency
            <select value={form.currency_code} onChange={set('currency_code')}>
              <option value="VUV">VUV</option>
              {currencies.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            {errors.currency_code && <small className="error">{errors.currency_code[0]}</small>}
          </label>
          <label>
            Invoice date
            <input type="date" value={form.invoice_date} onChange={set('invoice_date')} max={today} />
            {errors.invoice_date && <small className="error">{errors.invoice_date[0]}</small>}
          </label>
          <label>
            Due date
            <input type="date" value={form.due_date} onChange={set('due_date')} />
          </label>
          <label>
            Payment method *
            <select value={form.payment_method} onChange={set('payment_method')} required>
              <option value="">Select…</option>
              {listOptions(lists, 'payment_method').map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Amount paid *
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={form.amount_paid}
              onChange={set('amount_paid')}
              required
            />
            {errors.amount_paid && <small className="error">{errors.amount_paid[0]}</small>}
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/procurement/payments">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
